import LogOutIcon from '@components/Icons/LogOutIcon';
import { getAvatarInitial, getColorFromString, getSpeakerInitials } from '@lib/utils/colorUtils';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { MouseEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import css from './UserMenu.module.scss';

// If bgColor not provided, it will be generated from the name.
// If initial is provided, it overrides the name-derived letter (used for
// nickname-first user avatars via getAvatarInitial).
interface ProfileAvatarProps {
  name?: string | null;
  size?: 'small' | 'large';
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  bgColor?: string;
  initial?: string;
}

export const ProfileAvatar = ({
  name,
  size = 'small',
  onClick,
  bgColor = undefined,
  initial,
}: ProfileAvatarProps) => {
  const avatarColor = bgColor || getColorFromString(name || '');
  const displayText = initial ?? getSpeakerInitials(name);

  return (
    <div
      className={classnames(
        size === 'small' ? css.profileAvatar : css.largeProfileAvatar,
        css.avatarWithBackground,
      )}
      onClick={onClick}
      style={
        {
          background: avatarColor,
        } as React.CSSProperties
      }
    >
      {displayText}
    </div>
  );
};

export const UserMenu = observer(() => {
  const nav = useNavigate();
  const location = useLocation();
  const user = userStore.data;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    trackingStore.userMenuOpened();
  };
  const handleClose = () => {
    setAnchorEl(null);
    trackingStore.userMenuClosed();
  };

  const logout = () => {
    trackingStore.userMenuItemClicked({ item: 'logout' });
    userStore.logout();
  };

  const goToSettings = () => {
    trackingStore.userMenuItemClicked({ item: 'settings', destination: '/settings' });
    if (location.pathname !== '/settings') {
      nav('/settings');
    }
  };

  return (
    <>
      {user !== null && (
        <>
          <ProfileAvatar name={user.email} initial={getAvatarInitial(user)} onClick={handleClick} />
          <Menu
            id="userMenu"
            className={css.menu}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
          >
            <div className={css.header}>
              <ProfileAvatar
                name={user.email}
                initial={getAvatarInitial(user)}
                size="large"
                onClick={handleClick}
              />
              {user.email ? user.email : ''}
            </div>
            <Divider />
            <MenuItem onClick={goToSettings}>
              <ListItemIcon>
                <SettingsRoundedIcon sx={{ fontSize: '20px' }} />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={logout}>
              <ListItemIcon>
                <LogOutIcon />
              </ListItemIcon>
              Log out
            </MenuItem>
            <Divider />
            <div className={css.footer}>
              <span
                className={css.footerItem}
                onClick={() => {
                  trackingStore.userMenuItemClicked({
                    item: 'privacy',
                    destination: 'https://starzero.ai/privacy/',
                  });
                  window.open('https://starzero.ai/privacy/', '_blank');
                }}
              >
                Privacy
              </span>
              <span
                className={css.footerItem}
                onClick={() => {
                  trackingStore.userMenuItemClicked({
                    item: 'terms',
                    destination: 'https://starzero.ai/terms/',
                  });
                  window.open('https://starzero.ai/terms/', '_blank');
                }}
              >
                Terms
              </span>
              <span className={css.footerItem}>Copyright</span>
              <LinkedInIcon
                className={css.lastFooterItem}
                onClick={() => {
                  trackingStore.userMenuItemClicked({
                    item: 'linkedin',
                    destination: 'https://www.linkedin.com/company/it-just-works-inc',
                  });
                  window.open('https://www.linkedin.com/company/it-just-works-inc', '_blank');
                }}
              />
            </div>
          </Menu>
        </>
      )}
    </>
  );
});
